/**
 * Generic type for enum objects
 */
export type EnumObject<T> = Record<string, T>;

/**
 * Function type for clamping enum values
 * @template T - The type of enum values
 */
export type EnumClampFunction<T> = (value: string, localDefaultValue?: T) => T;

/**
 * Adds a DEFAULT property to an enum object
 * @template T - The type of enum values
 * @param enumObject - The enum object to modify
 * @param defaultValue - The default value to assign
 */
export const defineDefaultEnumValue = <T>(enumObject: EnumObject<T>, defaultValue: T): void => {
  Object.defineProperty(enumObject, 'DEFAULT', {
    value: defaultValue,
    enumerable: false,
  });
};

/**
 * Create enum clamp function
 * @template T - The type of enum values
 * @param enumObject - The enum object
 * @param defaultValue - Default enum value (optional)
 * @returns A clamp function that validates enum values
 *
 * @example
 * ```typescript
 * const STATUS = {
 *   active: 'active',
 *   inactive: 'inactive',
 *   pending: 'pending'
 * } as const;
 *
 * const clampStatus = createEnumClampFunction(STATUS, STATUS.inactive);
 *
 * clampStatus('active');     // Returns 'active'
 * clampStatus('invalid');    // Returns 'inactive' (default)
 * clampStatus('invalid', STATUS.pending); // Returns 'pending' (local default)
 * ```
 */
export function createEnumClampFunction<T>(
  enumObject: EnumObject<T>,
  defaultValue: null
): (value: string, localDefaultValue?: T | null) => T | null;

// eslint-disable-next-line no-redeclare
export function createEnumClampFunction<T>(
  enumObject: EnumObject<T>,
  defaultValue?: T
): EnumClampFunction<T>;

// eslint-disable-next-line no-redeclare
export function createEnumClampFunction<T>(
  enumObject: EnumObject<T>,
  defaultValue?: T | null
): (value: string, localDefaultValue?: T | null) => T | null {
  const values = Object.values(enumObject);

  return (value: string, localDefaultValue?: T | null): T | null => {
    if (values.includes(value as T)) {
      return value as T;
    }

    if (localDefaultValue !== undefined) {
      return localDefaultValue;
    }
    if (defaultValue !== undefined) {
      return defaultValue;
    }
    return values[0];
  };
}

/**
 * Adds a clamp function to an enum object
 * @template T - The type of enum values
 * @param enumObject - The enum object to modify
 * @param defaultValue - Default enum value (optional)
 *
 * @example
 * ```typescript
 * const STATUS = {
 *   active: 'active',
 *   inactive: 'inactive',
 *   pending: 'pending'
 * } as const;
 *
 * defineEnumClampFunction(STATUS, STATUS.INACTIVE);
 *
 * // Now STATUS.clamp is available
 * STATUS.clamp('active');     // Returns 'active'
 * STATUS.clamp('invalid');    // Returns 'inactive'
 * ```
 */
export const defineEnumClampFunction = <T>(enumObject: EnumObject<T>, defaultValue?: T): void => {
  Object.defineProperty(enumObject, 'clamp', {
    value: createEnumClampFunction(enumObject, defaultValue),
    enumerable: false,
  });
};
